import { Amplify } from "aws-amplify";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";

// App Auth Actions
import ACTIONS from "@/Constants/actionsList";

// Styles
import "./App.css";

// Components
import ProtectedRoute from "@Auth/Routes/ProtectedRoute";

/************************************************ Views ************************************************/
import Dashboard from "@/Dashboard";
// Auth
import Login from "@/Auth/Login";

import NotFound from "@/Layouts/views/NotFound";
import AccessDenied from "@/Layouts/views/AccessDenied";
import UserPreferences from "@/User/views/UserPreferences";
import UserProfile from "@/User/views/UserProfile";
import ResetPassword from "@/Auth/ResetPassword";
import NewPassword from "@/Auth/NewPassword";
import ResetPasswordVerification from "@/Auth/ResetPasswordVerification";
import ExpiredLink from "@/Auth/ExpiredLink";
import MultiFactorAuthentication from "@/Auth/MultiFactorAuthentication";
// Settings
import SystemSettings from "@/Settings/views/SystemSettings";
import AdministrationTools from "@/Settings/views/AdministrationTools";
import SalesPerformanceReportFormats from "@/Settings/views/SalesPerformanceReportFormats";
import PositionMapping from "@/Settings/views/PositionMapping";
import UserAccessSettings from "@/Settings/views/UserAccessSettings";
import GroupAccessSettings from "@/Settings/views/GroupAccessSettings";
import PayrollMappingView from "./Settings/views/PayrollMapping";
import PtoModalityView from "@/Settings/views/PtoModality";
import QueueMapping from "@/Settings/views/QueueMapping";
import FinancialStatementConfig from "@/Settings/views/FinancialStatementConfig";
// Motivators
// Sales
import SpiffSetupView from "@Motivators/views/Spiffs/Setup/Sales";
import SpiffAwardsView from "@Motivators/views/Spiffs/Awards/Sales";
import SpecialUnitsView from "@Motivators/views/SpecialUnits";
import AutoAlertsView from "@Motivators/views/AutoAlerts";
// Service
import SpiffBonusView from "@Motivators/views/Spiffs/Setup/Service";
import SpiffBonusAwardsView from "@Motivators/views/Spiffs/Awards/Service";
import TrainingView from "@Motivators/views/Training";
import { SalesTemporaryGuaranteedView, TemporaryGuaranteedView } from "@Motivators/views/TemporaryGuaranteed";
// My Motivators
import MyMotivators from "@Motivators/views/MyMotivators";

// Pay Plans
import SalesAssociates from "@PayPlans/views/Departments/Sales/Associates";
import SalesManagers from "@PayPlans/views/Departments/Sales/Managers";
import GeneralSalesManagers from "@PayPlans/views/Departments/Sales/GeneralManagers";

import FinanceManagers from "@PayPlans/views/Departments/Finance/Managers";
import FinanceDirectors from "@PayPlans/views/Departments/Finance/Directors";

import BodyShopManagers from "@PayPlans/views/Departments/BodyShop/Managers";
import BodyShopPainters from "@PayPlans/views/Departments/BodyShop/Painters";
import BodyShopProdManagers from "@/PayPlans/views/Departments/BodyShop/ProductManagers";
import BodyShopEstimators from "@PayPlans/views/Departments/BodyShop/Estimators";
import BodyShopTechnicians from "@PayPlans/views/Departments/BodyShop/Technicians";
import BodyShopForeman from "@PayPlans/views/Departments/BodyShop/Foreman";

import PartsCounterAssociates from "@PayPlans/views/Departments/Parts/CounterAssociates";
import PartsDirectors from "@PayPlans/views/Departments/Parts/Directors";
import PartsManagers from "@PayPlans/views/Departments/Parts/Managers";

import ServiceDirectors from "@PayPlans/views/Departments/Service/Directors";
import ServiceManagers from "@PayPlans/views/Departments/Service/Managers";
import ServiceAdvisors from "@PayPlans/views/Departments/Service/Advisors";
import InternalServiceAdvisors from "@PayPlans/views/Departments/Service/InternalAdvisors";
import ServiceTechnicians from "@PayPlans/views/Departments/Service/Technicians";
import ServiceForeman from "@PayPlans/views/Departments/Service/Foreman";

// My Pay Plans
import MyPayPlansView from "@PayPlans/views/MyPayPlans";

// Pay Plans Assignments
import CurrentAssignments from "@PayPlans/views/Assignments/Current";
import IndividualAssignments from "@PayPlans/views/Assignments/Individual";
import GroupAssignments from "@PayPlans/views/Assignments/Group";
import HistoricalAssignments from "@PayPlans/views/Assignments/Historical";

// Washouts
import SalesAssociateWashout from "@Washouts/views/Departments/Sales/Associates";
import SalesManagerWashout from "@Washouts/views/Departments/Sales/Managers";
import GeneralSalesManagerWashout from "@Washouts/views/Departments/Sales/GeneralManagers";

import FinanceManagerWashout from "@Washouts/views/Departments/Finance/Managers";
import FinanceDirectorWashout from "@Washouts/views/Departments/Finance/Directors";

import ServiceManagerWashout from "@Washouts/views/Departments/Service/Managers";
import ServiceDirectorWashout from "@Washouts/views/Departments/Service/Directors";
import ServiceForemanWashout from "@Washouts/views/Departments/Service/Foreman";
import ServiceTechnicianWashout from "@Washouts/views/Departments/Service/Technicians";
import ServiceAdvisorWashout from "./Washouts/views/Departments/Service/Advisors";
import InternalServiceAdvisorWashout from "@Washouts/views/Departments/Service/InternalAdvisors";

import BodyShopManagerWashout from "@Washouts/views/Departments/BodyShop/Managers";
import BodyShopProductManagerWashout from "@Washouts/views/Departments/BodyShop/ProductManagers";
import BodyShopForemanWashout from "@Washouts/views/Departments/BodyShop/Foreman";
import BodyShopTechnicianWashout from "@Washouts/views/Departments/BodyShop/Technicians";
import BodyShopPainterWashout from "@Washouts/views/Departments/BodyShop/Painters";
import BodyShopEstimatorWashout from "@Washouts/views/Departments/BodyShop/Estimators";

import PartsManagerWashout from "@Washouts/views/Departments/Parts/Managers";
import PartsDirectorWashout from "@Washouts/views/Departments/Parts/Directors";
import PartsCounterAssociateWashout from "@Washouts/views/Departments/Parts/CounterAssociates";

import MyWashouts from "@/Washouts/views/MyWashouts";
import SupportCenter from "@/Washouts/views/SupportCenter";
import PayrollClosing from "@/Washouts/views/PayrollClosing";
import WashoutReview from "@/Washouts/views/WashoutReview";

// Data Pages
import Chargebacks from "@DataPages/views/Chargebacks";
import OemData from "@DataPages/views/OEM";
import Users from "@DataPages/views/Users";
import Draw from "@DataPages/views/Draw";
import TimeAndPayRates from "@DataPages/views/TimeAndPayRates";
import Cit from "@DataPages/views/Cit";
import CallActivity from "@DataPages/views/CallActivity";
import CustomerEngagementTrail from "@DataPages/views/CustomerEngagementTrail";
import Inventory from "@DataPages/views/Inventory";
import CRM from "@/DataPages/views/CRM";
import ServiceOperations from "@/DataPages/views/ServiceOperations";

// My OEM DATA
import MyOEM from "@DataPages/views/MyOEM";
// My CIT
import MyCIT from "@DataPages/views/MyCIT";

// Miscellaneous
import { SupportRequests } from "@/Miscellaneous/views/SupportRequests";
import OrganizationChart from "@Miscellaneous/views/OrganizationChart";
import MyCallActivity from "@/DataPages/components/MyCallActivity";
import { pdfjs } from "react-pdf";
import UnprotectedRoute from "./Auth/Routes/UnprotectedRoute";
import { FilterStateStoreAutoClearer } from "@Miscellaneous/components/FilterStateStoreAutoClearer";
import SalesOperations from "@DataPages/views/SalesOperations";
import LocationAccessGuard from "@Auth/Guards/LocationAccessGuard";
import { ThemeProvider } from "@mui/material/styles";
import { useTheme } from "@/themeContext.tsx";
import CssBaseline from "@mui/material/CssBaseline";

pdfjs.GlobalWorkerOptions.workerSrc = new URL("pdfjs-dist/build/pdf.worker.min.mjs", import.meta.url).toString();
/********************************************** End Routes **********************************************/

Amplify.configure({
	Auth: {
		Cognito: {
			userPoolId: import.meta.env.VITE_APP_AWS_COGNITO_USER_POOL_ID,
			userPoolClientId: import.meta.env.VITE_APP_AWS_COGNITO_USER_POOL_WEB_CLIENT_ID,
		},
	},
	API: {
		GraphQL: {
			region: import.meta.env.VITE_APP_AWS_REGION,
			endpoint: `${import.meta.env.VITE_APP_AWS_APPSYNC_RTC}`,
			defaultAuthMode: "userPool",
		},
	},
});

function App() {
	const { theme } = useTheme();
	return (
		<ThemeProvider theme={theme}>
			<CssBaseline />
			<BrowserRouter>
				<LocationAccessGuard>
					<FilterStateStoreAutoClearer />
					<Routes>
						<Route
							path="/login"
							element={
								<UnprotectedRoute
									component={(tabId: string, messageMethod: Function) => (
										<Login tabId={tabId} messageMethod={messageMethod} />
									)}
								/>
							}
						/>
						<Route
							path="/reset-password"
							element={
								<UnprotectedRoute>
									<NewPassword />
								</UnprotectedRoute>
							}
						/>
						<Route
							path="/reset"
							element={
								<UnprotectedRoute>
									<ResetPassword />
								</UnprotectedRoute>
							}
						/>
						<Route
							path="/reset/:userName"
							element={
								<UnprotectedRoute>
									<ResetPassword />
								</UnprotectedRoute>
							}
						/>
						<Route
							path="/reset-password-verification/:code/:userName"
							element={
								<UnprotectedRoute>
									<ResetPasswordVerification />
								</UnprotectedRoute>
							}
						/>
						<Route
							path="/link-expired"
							element={
								<UnprotectedRoute>
									<ExpiredLink />
								</UnprotectedRoute>
							}
						/>
						<Route
							path="/multi-factor-authentication"
							element={
								<UnprotectedRoute>
									<MultiFactorAuthentication />
								</UnprotectedRoute>
							}
						/>
						<Route path="/" element={<Navigate to="/dashboard" />} />
						<Route
							path="/dashboard"
							element={
								<ProtectedRoute actions={[ACTIONS.VIEW_DASHBOARD]}>
									<Dashboard />
								</ProtectedRoute>
							}
						/>
						<Route
							path="/user-preferences"
							element={
								<ProtectedRoute actions={[ACTIONS.VIEW_USER_PREFERENCES]}>
									<UserPreferences />
								</ProtectedRoute>
							}
						/>
						<Route
							path="/user-profile"
							element={
								<ProtectedRoute actions={[ACTIONS.VIEW_USER_PROFILE]}>
									<UserProfile />
								</ProtectedRoute>
							}
						/>
						{/********************************** SETTINGS **********************************/}
						<Route
							path="/financial-statement-config"
							element={
								<ProtectedRoute actions={[ACTIONS.VIEW_FINANCIAL_STATEMENT_CONFIG]}>
									<FinancialStatementConfig />
								</ProtectedRoute>
							}
						/>
						<Route
							path="/queue-mapping"
							element={
								<ProtectedRoute actions={[ACTIONS.VIEW_QUEUE_MAPPINGS]}>
									<QueueMapping />
								</ProtectedRoute>
							}
						/>
						<Route
							path="/pto-modality"
							element={
								<ProtectedRoute actions={[ACTIONS.VIEW_PTO_MODALITY]}>
									<PtoModalityView />
								</ProtectedRoute>
							}
						/>
						<Route
							path="/payroll-mappings"
							element={
								<ProtectedRoute actions={[ACTIONS.VIEW_PAY_ITEMS_MAPPING, ACTIONS.VIEW_CUSTOM_FIELD_ITEMS_MAPPING]}>
									<PayrollMappingView />
								</ProtectedRoute>
							}
						/>
						<Route
							path="/group-settings"
							element={
								<ProtectedRoute actions={[ACTIONS.VIEW_ACTION_GROUPS]}>
									<GroupAccessSettings />
								</ProtectedRoute>
							}
						/>
						<Route
							path="/user-access-settings"
							element={
								<ProtectedRoute actions={[ACTIONS.VIEW_POSITION_MAPPINGS]}>
									<UserAccessSettings />
								</ProtectedRoute>
							}
						/>
						<Route
							path="/position-mapping"
							element={
								<ProtectedRoute actions={[ACTIONS.VIEW_POSITION_MAPPINGS]}>
									<PositionMapping />
								</ProtectedRoute>
							}
						/>
						<Route
							path="/sales-performance-report-formats"
							element={
								<ProtectedRoute actions={[ACTIONS.VIEW_SALES_PERFORMANCE_REPORT_FORMATS]}>
									<SalesPerformanceReportFormats />
								</ProtectedRoute>
							}
						/>
						<Route
							path="/administration-tools"
							element={
								<ProtectedRoute actions={[ACTIONS.VIEW_ADMINISTRATION_TOOLS]}>
									<AdministrationTools />
								</ProtectedRoute>
							}
						/>
						<Route
							path="/system-settings"
							element={
								<ProtectedRoute actions={[ACTIONS.VIEW_SYSTEM_SETTINGS]}>
									<SystemSettings />
								</ProtectedRoute>
							}
						/>
						{/********************************** WASHOUTS **********************************/}
						<Route path="/washouts/*">
							<Route
								path="sales-associate"
								element={
									<ProtectedRoute actions={[ACTIONS.VIEW_SALES_ASSOCIATE_WASHOUTS]}>
										<SalesAssociateWashout />
									</ProtectedRoute>
								}
							/>
							<Route
								path="finance-manager"
								element={
									<ProtectedRoute actions={[ACTIONS.VIEW_FINANCE_MANAGER_WASHOUTS]}>
										<FinanceManagerWashout />
									</ProtectedRoute>
								}
							/>
							<Route
								path="finance-director"
								element={
									<ProtectedRoute actions={[ACTIONS.VIEW_FINANCE_DIRECTOR_WASHOUTS]}>
										<FinanceDirectorWashout />
									</ProtectedRoute>
								}
							/>
							<Route
								path="sales-manager"
								element={
									<ProtectedRoute actions={[ACTIONS.VIEW_FINANCE_MANAGER_WASHOUTS]}>
										<SalesManagerWashout />
									</ProtectedRoute>
								}
							/>
							<Route
								path="general-sales-manager"
								element={
									<ProtectedRoute actions={[ACTIONS.VIEW_GENERAL_SALES_MANAGER_WASHOUTS]}>
										<GeneralSalesManagerWashout />
									</ProtectedRoute>
								}
							/>
							<Route
								path="service-manager"
								element={
									<ProtectedRoute actions={[ACTIONS.VIEW_SERVICE_MANAGER_WASHOUTS]}>
										<ServiceManagerWashout />
									</ProtectedRoute>
								}
							/>
							<Route
								path="service-director"
								element={
									<ProtectedRoute actions={[ACTIONS.VIEW_SERVICE_DIRECTOR_WASHOUTS]}>
										<ServiceDirectorWashout />
									</ProtectedRoute>
								}
							/>
							<Route
								path="service-foreman"
								element={
									<ProtectedRoute actions={[ACTIONS.VIEW_SERVICE_FOREMAN_WASHOUTS]}>
										<ServiceForemanWashout />
									</ProtectedRoute>
								}
							/>
							<Route
								path="service-advisor"
								element={
									<ProtectedRoute actions={[ACTIONS.VIEW_SERVICE_ADVISOR_WASHOUTS]}>
										<ServiceAdvisorWashout />
									</ProtectedRoute>
								}
							/>
							<Route
								path="internal-service-advisor"
								element={
									<ProtectedRoute actions={[ACTIONS.VIEW_INTERNAL_SERVICE_ADVISOR_WASHOUTS]}>
										<InternalServiceAdvisorWashout />
									</ProtectedRoute>
								}
							/>
							<Route
								path="body-shop-manager"
								element={
									<ProtectedRoute actions={[ACTIONS.VIEW_BODY_SHOP_MANAGER_WASHOUTS]}>
										<BodyShopManagerWashout />
									</ProtectedRoute>
								}
							/>

							<Route
								path="body-shop-product-manager"
								element={
									<ProtectedRoute actions={[ACTIONS.VIEW_BODY_SHOP_PRODUCT_MANAGER_WASHOUTS]}>
										<BodyShopProductManagerWashout />
									</ProtectedRoute>
								}
							/>
							<Route
								path="body-shop-foreman"
								element={
									<ProtectedRoute actions={[ACTIONS.VIEW_BODY_SHOP_FOREMAN_WASHOUTS]}>
										<BodyShopForemanWashout />
									</ProtectedRoute>
								}
							/>
							<Route
								path="body-shop-estimator"
								element={
									<ProtectedRoute actions={[ACTIONS.VIEW_BODY_SHOP_ESTIMATOR_WASHOUTS]}>
										<BodyShopEstimatorWashout />
									</ProtectedRoute>
								}
							/>
							<Route
								path="parts-manager"
								element={
									<ProtectedRoute actions={[ACTIONS.VIEW_PARTS_MANAGER_WASHOUTS]}>
										<PartsManagerWashout />
									</ProtectedRoute>
								}
							/>
							<Route
								path="parts-director"
								element={
									<ProtectedRoute actions={[ACTIONS.VIEW_PARTS_DIRECTOR_WASHOUTS]}>
										<PartsDirectorWashout />
									</ProtectedRoute>
								}
							/>
							<Route
								path="parts-counter-associate"
								element={
									<ProtectedRoute actions={[ACTIONS.VIEW_PARTS_COUNTER_ASSOCIATE_WASHOUTS]}>
										<PartsCounterAssociateWashout />
									</ProtectedRoute>
								}
							/>
							<Route
								path="service-technician"
								element={
									<ProtectedRoute actions={[ACTIONS.VIEW_SERVICE_TECHNICIAN_WASHOUTS]}>
										<ServiceTechnicianWashout />
									</ProtectedRoute>
								}
							/>
							<Route
								path="body-shop-technician"
								element={
									<ProtectedRoute actions={[ACTIONS.VIEW_BODY_SHOP_TECHNICIAN_WASHOUTS]}>
										<BodyShopTechnicianWashout />
									</ProtectedRoute>
								}
							/>
							<Route
								path="body-shop-painter"
								element={
									<ProtectedRoute actions={[ACTIONS.VIEW_BODY_SHOP_PAINTER_WASHOUTS]}>
										<BodyShopPainterWashout />
									</ProtectedRoute>
								}
							/>

							<Route
								path="personal"
								element={
									<ProtectedRoute actions={[ACTIONS.VIEW_MY_WASHOUTS]}>
										<MyWashouts />
									</ProtectedRoute>
								}
							/>

							<Route path="*" element={<NotFound />} />
						</Route>
						<Route
							path="/support-center"
							element={
								<ProtectedRoute actions={[ACTIONS.VIEW_SUPPORT_CENTER]}>
									<SupportCenter />
								</ProtectedRoute>
							}
						/>
						<Route
							path="/payroll-closing"
							element={
								<ProtectedRoute actions={[ACTIONS.VIEW_COMMISSION_CYCLE_CLOSING]}>
									<PayrollClosing />
								</ProtectedRoute>
							}
						/>
						<Route
							path="/washout-review"
							element={
								<ProtectedRoute actions={[ACTIONS.VIEW_WASHOUT_REVIEW]}>
									<WashoutReview />
								</ProtectedRoute>
							}
						/>
						{/********************************** PAY PLANS **********************************/}
						<Route path="/pay-plans/*">
							<Route
								path="sales-associates"
								element={
									<ProtectedRoute actions={[ACTIONS.VIEW_SALES_ASSOCIATE_PAY_PLANS]}>
										<SalesAssociates />
									</ProtectedRoute>
								}
							/>
							<Route
								path="sales-managers"
								element={
									<ProtectedRoute actions={[ACTIONS.VIEW_SALES_MANAGER_PAY_PLANS]}>
										<SalesManagers />
									</ProtectedRoute>
								}
							/>
							<Route
								path="general-sales-managers"
								element={
									<ProtectedRoute actions={[ACTIONS.VIEW_GENERAL_SALES_MANAGER_PAY_PLANS]}>
										<GeneralSalesManagers />
									</ProtectedRoute>
								}
							/>

							<Route
								path="body-shop-managers"
								element={
									<ProtectedRoute actions={[ACTIONS.VIEW_BODY_SHOP_MANAGER_PAY_PLANS]}>
										<BodyShopManagers />
									</ProtectedRoute>
								}
							/>
							<Route
								path="body-shop-painters"
								element={
									<ProtectedRoute actions={[ACTIONS.VIEW_BODY_SHOP_PAINTER_PAY_PLANS]}>
										<BodyShopPainters />
									</ProtectedRoute>
								}
							/>
							<Route
								path="body-shop-product-managers"
								element={
									<ProtectedRoute actions={[ACTIONS.VIEW_BODY_SHOP_PRODUCT_MANAGER_PAY_PLANS]}>
										<BodyShopProdManagers />
									</ProtectedRoute>
								}
							/>
							<Route
								path="body-shop-estimators"
								element={
									<ProtectedRoute actions={[ACTIONS.VIEW_BODY_SHOP_ESTIMATOR_PAY_PLANS]}>
										<BodyShopEstimators />
									</ProtectedRoute>
								}
							/>
							<Route
								path="body-shop-technicians"
								element={
									<ProtectedRoute actions={[ACTIONS.VIEW_BODY_SHOP_TECHNICIAN_PAY_PLANS]}>
										<BodyShopTechnicians />
									</ProtectedRoute>
								}
							/>
							<Route
								path="body-shop-foreman"
								element={
									<ProtectedRoute actions={[ACTIONS.VIEW_BODY_SHOP_FOREMAN_PAY_PLANS]}>
										<BodyShopForeman />
									</ProtectedRoute>
								}
							/>

							<Route
								path="finance-directors"
								element={
									<ProtectedRoute actions={[ACTIONS.VIEW_FINANCE_DIRECTOR_PAY_PLANS]}>
										<FinanceDirectors />
									</ProtectedRoute>
								}
							/>
							<Route
								path="finance-managers"
								element={
									<ProtectedRoute actions={[ACTIONS.VIEW_FINANCE_MANAGER_PAY_PLANS]}>
										<FinanceManagers />
									</ProtectedRoute>
								}
							/>

							<Route
								path="parts-counter-associates"
								element={
									<ProtectedRoute actions={[ACTIONS.VIEW_PARTS_COUNTER_ASSOCIATE_PAY_PLANS]}>
										<PartsCounterAssociates />
									</ProtectedRoute>
								}
							/>
							<Route
								path="parts-directors"
								element={
									<ProtectedRoute actions={[ACTIONS.VIEW_PARTS_DIRECTOR_PAY_PLANS]}>
										<PartsDirectors />
									</ProtectedRoute>
								}
							/>
							<Route
								path="parts-managers"
								element={
									<ProtectedRoute actions={[ACTIONS.VIEW_PARTS_MANAGER_PAY_PLANS]}>
										<PartsManagers />
									</ProtectedRoute>
								}
							/>

							<Route
								path="service-directors"
								element={
									<ProtectedRoute actions={[ACTIONS.VIEW_SERVICE_DIRECTOR_PAY_PLANS]}>
										<ServiceDirectors />
									</ProtectedRoute>
								}
							/>
							<Route
								path="service-managers"
								element={
									<ProtectedRoute actions={[ACTIONS.VIEW_SERVICE_MANAGER_PAY_PLANS]}>
										<ServiceManagers />
									</ProtectedRoute>
								}
							/>
							<Route
								path="service-advisors"
								element={
									<ProtectedRoute actions={[ACTIONS.VIEW_SERVICE_ADVISOR_PAY_PLANS]}>
										<ServiceAdvisors />
									</ProtectedRoute>
								}
							/>
							<Route
								path="internal-service-advisors"
								element={
									<ProtectedRoute actions={[ACTIONS.VIEW_INTERNAL_SERVICE_ADVISOR_PAY_PLANS]}>
										<InternalServiceAdvisors />
									</ProtectedRoute>
								}
							/>
							<Route
								path="service-technicians"
								element={
									<ProtectedRoute actions={[ACTIONS.VIEW_SERVICE_TECHNICIAN_PAY_PLANS]}>
										<ServiceTechnicians />
									</ProtectedRoute>
								}
							/>
							<Route
								path="service-foreman"
								element={
									<ProtectedRoute actions={[ACTIONS.VIEW_SERVICE_FOREMAN_PAY_PLANS]}>
										<ServiceForeman />
									</ProtectedRoute>
								}
							/>

							{/* MY PAY PLANS */}
							<Route
								path="personal"
								element={
									<ProtectedRoute actions={[ACTIONS.VIEW_MY_PAY_PLANS]}>
										<MyPayPlansView />
									</ProtectedRoute>
								}
							/>

							{/* ASSIGNMENTS */}
							<Route
								path="current-assignments"
								element={
									<ProtectedRoute actions={[ACTIONS.VIEW_PAY_PLAN_ASSIGNMENTS]}>
										<CurrentAssignments />
									</ProtectedRoute>
								}
							/>
							<Route
								path="individual-assignments"
								element={
									<ProtectedRoute actions={[ACTIONS.VIEW_PAY_PLAN_ASSIGNMENTS]}>
										<IndividualAssignments />
									</ProtectedRoute>
								}
							/>
							<Route
								path="group-assignments"
								element={
									<ProtectedRoute actions={[ACTIONS.VIEW_PAY_PLAN_ASSIGNMENTS]}>
										<GroupAssignments />
									</ProtectedRoute>
								}
							/>

							<Route
								path="history-assignments"
								element={
									<ProtectedRoute actions={[ACTIONS.VIEW_PAY_PLAN_ASSIGNMENTS]}>
										<HistoricalAssignments />
									</ProtectedRoute>
								}
							/>

							<Route path="*" element={<NotFound />} />
						</Route>
						{/********************************** DATA PAGES **********************************/}
						<Route
							path="/sales-operations"
							element={
								<ProtectedRoute actions={[ACTIONS.VIEW_SALES_LOG, ACTIONS.VIEW_SALES_PERFORMANCE_REPORTS]}>
									<SalesOperations />
								</ProtectedRoute>
							}
						/>
						<Route
							path="/chargebacks"
							element={
								<ProtectedRoute actions={[ACTIONS.VIEW_CHARGEBACKS_REPORTS]}>
									<Chargebacks />
								</ProtectedRoute>
							}
						/>
						<Route
							path="/oem-data"
							element={
								<ProtectedRoute actions={[ACTIONS.VIEW_CUSTOMER_SATISFACTION_SCORES]}>
									<OemData />
								</ProtectedRoute>
							}
						/>
						<Route
							path="/users"
							element={
								<ProtectedRoute actions={[ACTIONS.VIEW_USERS]}>
									<Users />
								</ProtectedRoute>
							}
						/>
						<Route
							path="/draw"
							element={
								<ProtectedRoute actions={[ACTIONS.VIEW_DRAW_AMOUNTS]}>
									<Draw />
								</ProtectedRoute>
							}
						/>
						<Route
							path="/time-and-pay-rates"
							element={
								<ProtectedRoute actions={[ACTIONS.VIEW_PAY_RATES]}>
									<TimeAndPayRates />
								</ProtectedRoute>
							}
						/>
						<Route
							path="/fni-management"
							element={
								<ProtectedRoute
									actions={[
										ACTIONS.VIEW_ALL_DEALS,
										ACTIONS.VIEW_CIT_REPORTS,
										ACTIONS.VIEW_FINANCE_DEAL_DATA,
										ACTIONS.VIEW_FINANCE_DETAILS,
										ACTIONS.VIEW_FINANCE_TRADES_PAYOFFS,
										ACTIONS.VIEW_FINANCE_TITLINGS,
										ACTIONS.VIEW_CANCELLATIONS,
									]}
								>
									<Cit />
								</ProtectedRoute>
							}
						/>
						<Route
							path="/call-activity-report"
							element={
								<ProtectedRoute actions={[ACTIONS.VIEW_CALL_ACTIVITY_REPORTS]}>
									<CallActivity />
								</ProtectedRoute>
							}
						/>
						<Route
							path="/customer-engagement-trail"
							element={
								<ProtectedRoute actions={[ACTIONS.VIEW_CUSTOMER_ENGAGEMENT_TRAIL]}>
									<CustomerEngagementTrail />
								</ProtectedRoute>
							}
						/>
						<Route
							path="/inventory"
							element={
								<ProtectedRoute actions={[ACTIONS.VIEW_INVENTORY]}>
									<Inventory />
								</ProtectedRoute>
							}
						/>
						<Route
							path="/my-oem-data"
							element={
								<ProtectedRoute actions={[ACTIONS.VIEW_MY_CUSTOMER_SATISFACTION_SCORES]}>
									<MyOEM />
								</ProtectedRoute>
							}
						/>
						<Route
							path="/my-cit"
							element={
								<ProtectedRoute actions={[ACTIONS.VIEW_MY_CIT_REPORTS]}>
									<MyCIT />
								</ProtectedRoute>
							}
						/>
						<Route
							path="/my-call-activity"
							element={
								<ProtectedRoute actions={[ACTIONS.VIEW_MY_CALL_ACTIVITY]}>
									<MyCallActivity />
								</ProtectedRoute>
							}
						/>
						<Route
							path="/customer-relation-management"
							element={
								<ProtectedRoute
									actions={[
										ACTIONS.VIEW_SALES_LEADS,
										ACTIONS.VIEW_DISPOSITIONS,
										ACTIONS.VIEW_SALES_LEAD_CUSTOMERS,
										ACTIONS.VIEW_SOCIAL_MEDIA_REVIEWS,
									]}
								>
									<CRM />
								</ProtectedRoute>
							}
						/>
						<Route
							path="/service-operations"
							element={
								<ProtectedRoute
									actions={[
										ACTIONS.VIEW_REPAIR_ORDERS,
										ACTIONS.VIEW_ADVISOR_PERFORMANCE,
										ACTIONS.VIEW_TECHNICIAN_FLAGGED_TIME,
									]}
								>
									<ServiceOperations />
								</ProtectedRoute>
							}
						/>
						{/********************************** MOTIVATORS **********************************/}
						<Route path="/motivators/*">
							{/* Sales */}
							<Route
								path="spiff-setups"
								element={
									<ProtectedRoute actions={[ACTIONS.VIEW_SALES_SPIFF_DEFINITIONS]}>
										<SpiffSetupView />
									</ProtectedRoute>
								}
							/>
							<Route
								path="spiff-awards"
								element={
									<ProtectedRoute actions={[ACTIONS.VIEW_SALES_SPIFF_AWARDS]}>
										<SpiffAwardsView />
									</ProtectedRoute>
								}
							/>
							<Route
								path="special-units"
								element={
									<ProtectedRoute actions={[ACTIONS.VIEW_SPECIAL_UNIT_LISTS]}>
										<SpecialUnitsView />
									</ProtectedRoute>
								}
							/>
							<Route
								path="opportunity-mining"
								element={
									<ProtectedRoute actions={[ACTIONS.VIEW_OPPORTUNITY_MINING]}>
										<AutoAlertsView />
									</ProtectedRoute>
								}
							/>
							<Route
								path="sales-temporary-guaranteed"
								element={
									<ProtectedRoute actions={[ACTIONS.VIEW_SALES_TEMP_GUARANTEED]}>
										<SalesTemporaryGuaranteedView />
									</ProtectedRoute>
								}
							/>
							{/* Service */}
							<Route
								path="spiff-bonus-setups"
								element={
									<ProtectedRoute actions={[ACTIONS.VIEW_SERVICE_SPIFF_DEFINITIONS]}>
										<SpiffBonusView />
									</ProtectedRoute>
								}
							/>
							<Route
								path="spiff-bonus-awards"
								element={
									<ProtectedRoute actions={[ACTIONS.VIEW_SERVICE_SPIFF_AWARDS]}>
										<SpiffBonusAwardsView />
									</ProtectedRoute>
								}
							/>
							<Route
								path="training"
								element={
									<ProtectedRoute actions={[ACTIONS.VIEW_SERVICES_TRAINING]}>
										<TrainingView />
									</ProtectedRoute>
								}
							/>
							<Route
								path="temporary-guaranteed"
								element={
									<ProtectedRoute actions={[ACTIONS.VIEW_TEMP_GUARANTEED]}>
										<TemporaryGuaranteedView />
									</ProtectedRoute>
								}
							/>
							<Route path="*" element={<NotFound />} />
						</Route>
						<Route
							path="/my-motivators"
							element={
								<ProtectedRoute actions={[ACTIONS.VIEW_MY_MOTIVATORS]}>
									<MyMotivators />
								</ProtectedRoute>
							}
						/>
						{/********************************* MISCELLANEOUS *********************************/}
						<Route
							path="/support-requests"
							element={
								<ProtectedRoute actions={[ACTIONS.VIEW_COMMISSION_SUPPORT]}>
									<SupportRequests />
								</ProtectedRoute>
							}
						/>
						<Route
							path="/organization-chart"
							element={
								<ProtectedRoute actions={[ACTIONS.VIEW_ORGANIZATION_CHART]}>
									<OrganizationChart />
								</ProtectedRoute>
							}
						/>
						{/********************************** ERROR PAGES **********************************/}
						<Route
							path="/access-denied"
							element={
								<ProtectedRoute actions={"*"}>
									<AccessDenied />
								</ProtectedRoute>
							}
						/>
						<Route
							path="*"
							element={
								<ProtectedRoute actions={"*"}>
									<NotFound />
								</ProtectedRoute>
							}
						/>
					</Routes>
				</LocationAccessGuard>
			</BrowserRouter>
		</ThemeProvider>
	);
}

export default App;
